import React, { useState, useEffect } from 'react';
import BackButton from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Lock, Eye, EyeSlash, EnvelopeSimple, Phone, FloppyDisk } from '@phosphor-icons/react';
import { toast } from 'sonner';
import LucentLoader from '@/components/LucentLoader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

export default function AdminProfile() {
  const { t } = useTranslation();
  const { direction, language } = useLanguage();
  const { user: authUser, setUser: setAuthUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setEmail(data.email || '');
      setPhone(data.phone || '');
    } catch {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await axios.put(`${API}/auth/profile`, { email, phone }, { withCredentials: true });
      toast.success(t('profile_updated'));
      if (data.user) setAuthUser({ ...authUser, ...data.user });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('profile_update_failed'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error(t('password_min_length'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('passwords_dont_match'));
      return;
    }
    setSavingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { withCredentials: true });
      toast.success(t('password_changed_success'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('password_change_failed'));
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return <LucentLoader />;
  }

  return (
    <div className="min-h-screen bg-[#F2F0FA]" dir={direction}>
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* User Info */}
        <Card className="border-[#C4BFF0]">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#655dc6] flex items-center justify-center text-white text-xl font-bold">
                {authUser?.name?.charAt(0) || 'A'}
              </div>
              <div>
                <CardTitle className="text-[#655dc6]">{authUser?.name || t('admin')}</CardTitle>
                <p className="text-sm text-[#655dc6]/60">{t('admin')}</p>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Edit Profile */}
        <Card className="border-[#C4BFF0]" data-testid="admin-edit-profile-card">
          <CardHeader>
            <CardTitle className="text-[#655dc6] flex items-center gap-2">
              <User size={22} weight="duotone" />
              {t('edit_profile')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileSave} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#655dc6]/80">{t('email')}</Label>
                  <div className="relative">
                    <EnvelopeSimple size={18} className="absolute start-3 top-1/2 -translate-y-1/2 text-[#655dc6]/50" />
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#E8E6F5] border-[#C4BFF0] ps-10" dir="ltr" data-testid="admin-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#655dc6]/80">{t('phone')}</Label>
                  <div className="relative">
                    <Phone size={18} className="absolute start-3 top-1/2 -translate-y-1/2 text-[#655dc6]/50" />
                    <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-[#E8E6F5] border-[#C4BFF0] ps-10" placeholder="+966 5XX XXX XXXX" dir="ltr" data-testid="admin-phone-input" />
                  </div>
                </div>
              </div>
              <Button type="submit" disabled={savingProfile} className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2" data-testid="admin-save-profile-btn">
                {savingProfile ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <FloppyDisk size={18} weight="bold" />}
                {t('save')}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="border-[#C4BFF0]" data-testid="admin-change-password-card">
          <CardHeader>
            <CardTitle className="text-[#655dc6] flex items-center gap-2">
              <Lock size={22} weight="duotone" />
              {t('change_password')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#655dc6]/80">{t('current_password')}</Label>
                <div className="relative">
                  <Input type={showCurrent ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="bg-[#E8E6F5] border-[#C4BFF0] pe-10" dir="ltr" data-testid="admin-current-password" />
                  <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute end-3 top-1/2 -translate-y-1/2 text-[#655dc6]/50">
                    {showCurrent ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#655dc6]/80">{t('new_password')}</Label>
                  <div className="relative">
                    <Input type={showNew ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="bg-[#E8E6F5] border-[#C4BFF0] pe-10" dir="ltr" data-testid="admin-new-password" />
                    <button type="button" onClick={() => setShowNew(!showNew)} className="absolute end-3 top-1/2 -translate-y-1/2 text-[#655dc6]/50">
                      {showNew ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#655dc6]/80">{t('confirm_new_password')}</Label>
                  <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="bg-[#E8E6F5] border-[#C4BFF0]" dir="ltr" data-testid="admin-confirm-password" />
                </div>
              </div>
              <Button type="submit" disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword} className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2" data-testid="admin-change-password-btn">
                {savingPassword ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : <Lock size={18} weight="bold" />}
                {t('change_password')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
